import { useState, useEffect, useCallback } from 'react';
import { Link, Head } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell from '../../components/layout/TeacherShell';
import { Card } from '../../components/ui';
import {
  WordCard,
  WordSearch,
  FrequencyBadge,
  TierBadge
} from '../../components/dictionary';
import { cn } from '../../lib/cn';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';

export default function DictionaryIndex({ featuredWords, stats, frequencyBands, beckTiers }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { clear } = useBreadcrumbs();
  useEffect(() => {
    // Section index is a dead-end relative to the vertical-nav axis; clear any
    // stale trail (e.g. book/chapter from a prior ChapterReader visit).
    clear();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/teacher/dictionary/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.data);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const hasSearchResults = searchQuery.trim() && searchResults.length > 0;
  const showFeatured = !searchQuery.trim();

  return (
    <TeacherShell scrollTitle={t('dictionary.title')}>
      <Head title={t('dictionary.vocabDictionary')} />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('dictionary.vocabDictionary')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            {t('dictionary.explore')} {stats.totalWords.toLocaleString()} {t('dictionary.wordsWithDefs')}
          </p>

          {/* Search */}
          <WordSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            results={searchResults}
            isLoading={isSearching}
            className="max-w-2xl mx-auto"
          />
        </header>

        {/* Stats */}
        {showFeatured && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            <StatCard label={t('dictionary.words')} value={stats.totalWords} />
            <StatCard label={t('dictionary.definitions')} value={stats.totalDefinitions} />
            <StatCard label={t('dictionary.morphemes')} value={stats.totalMorphemes} />
            <StatCard label={t('dictionary.wordFamilies')} value={stats.totalFamilies} />
            <StatCard label={t('dictionary.vocabularyLists')} value={stats.totalLists} href="/teacher/vocabulary_lists" />
          </div>
        )}

        {/* Search Results */}
        {hasSearchResults && (
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('dictionary.searchResults')}
              <span className="text-gray-500 dark:text-gray-400 font-normal ml-2">
                ({searchResults.length} {t('dictionary.found')})
              </span>
            </h2>
            <div className="space-y-3">
              {searchResults.map((word) => (
                <WordCard key={word.id} word={word} variant="list" />
              ))}
            </div>
          </section>
        )}

        {/* No Results */}
        {searchQuery.trim() && !isSearching && searchResults.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('dictionary.noWordsFound')} "{searchQuery}"
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              {t('dictionary.tryDifferent')}
            </p>
          </div>
        )}

        {/* Featured Words */}
        {showFeatured && (
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('dictionary.rareWords')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('dictionary.rareWordsDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredWords.map((word) => (
                <WordCard key={word.id} word={word} variant="card" />
              ))}
            </div>
          </section>
        )}

        {/* Browse by Category */}
        {showFeatured && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('dictionary.browseByCategory')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Frequency Band */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  {t('dictionary.byFrequencyBand')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('dictionary.frequencyBandDesc')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {frequencyBands.map((band) => (
                    <FrequencyBadge key={band} band={band} size="md" />
                  ))}
                </div>
              </Card>

              {/* By Beck Tier */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  {t('dictionary.byBeckTier')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('dictionary.beckTierDesc')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {beckTiers.map((tier) => (
                    <TierBadge key={tier} tier={tier} size="md" showTooltip />
                  ))}
                </div>
              </Card>
            </div>
          </section>
        )}
      </div>
    </TeacherShell>
  );
}

function StatCard({ label, value, href }) {
  const content = (
    <Card className={cn("p-4 text-center", href && "hover:shadow-md transition-shadow cursor-pointer")}>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {label}
      </div>
    </Card>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }

  return content;
}
